import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import ScrollReveal from "../Common/ScrollReveal";
import { useGetBlogsQuery } from "../../store/blogsApi";

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

/** Render content string: paragraphs and **bold** */
const renderContent = (content) => {
  if (!content) return null;
  return content.split(/\n\n+/).map((paragraph, i) => {
    const parts = paragraph.split(/(\*\*[^*]+\*\*)/g);
    return (
      <p key={i} className="mb-4 text-[#333] text-base md:text-lg leading-relaxed">
        {parts.map((part, j) =>
          part.startsWith("**") && part.endsWith("**") ? (
            <strong key={j} className="font-bold text-black">
              {part.slice(2, -2)}
            </strong>
          ) : (
            <span key={j}>{part}</span>
          )
        )}
      </p>
    );
  });
};

const BlogArticle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: blogs = [], isLoading, isError } = useGetBlogsQuery();
  const blog = blogs.find((b) => String(b.id) === id);

  if (!blog) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8f9fa] to-white font-[Segoe_UI,Arial,sans-serif] flex flex-col items-center justify-center px-4">
        <h1 className="text-2xl font-bold text-black mb-4">Article not found</h1>
        <button
          type="button"
          onClick={() => navigate("/blogs")}
          className="px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-[#333] transition-colors"
        >
          Back to Blogs
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8f9fa] to-white font-[Segoe_UI,Arial,sans-serif]">
      <ScrollReveal variant="fadeUp">
      <article className="max-w-[800px] mx-auto px-4 py-8 md:py-12 pb-16 md:pb-24">
        <button
          type="button"
          onClick={() => navigate("/blogs")}
          className="text-[#333] hover:text-black text-sm font-semibold mb-6 flex items-center gap-1.5 transition-colors"
        >
          ← All Blogs
        </button>
        <div className="bg-white rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-[#e8e8e8]">
          <div className="relative w-full h-[240px] sm:h-[280px] md:h-[320px] overflow-hidden">
            <img
              src={blog.image}
              alt={blog.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-6 md:p-10">
            <h1 className="text-2xl md:text-3xl font-black text-black mb-4 leading-tight tracking-wide">
              {blog.title}
            </h1>
            <div className="flex flex-wrap gap-4 mb-8 text-[#666] text-sm font-medium">
            <span className="flex items-center gap-1.5">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              {blog.author}
            </span>
            <span className="flex items-center gap-1.5">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {formatDate(blog.uploadedAt)}
            </span>
            </div>
            <div className="prose prose-lg max-w-none">
              {renderContent(blog.description)}
            </div>
          </div>
        </div>
      </article>
      </ScrollReveal>
    </div>
  );
};

export default BlogArticle;
